import { HttpClient, HttpErrorResponse, HttpParams } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { rxResource } from '@angular/core/rxjs-interop';
import { catchError, forkJoin, map, Observable, of, throwError } from 'rxjs';

import { ServiceResponseV1 } from '@core/models/apis';
import {
  DailyResultDay,
  DailyResultsBacklogV1,
  DailyResultsQueueState,
  DailyResultsRescoreRange,
  DailyResultsRescoreResponseV1,
  DailyResultsSimulationRequestV1,
  DailyResultV1
} from '@core/models/daily-results';
import { environment } from '@src/environments/environment';

export interface DailyResultDaysKey {
  youtopianId: string;
  dates: string[];
}

// The most rows the engine's backlog route returns in one read.
const BACKLOG_PAGE = 200;

// The engine answers an unscored day with a 404 that has no body. A 404 that carries one came from the gateway
// or the host, which means the route was not served and says nothing about the day.
const isUnscoredDay = (err: unknown): boolean =>
  err instanceof HttpErrorResponse && err.status === 404 && (err.error === null || err.error === '');

@Injectable({
  providedIn: 'root'
})
export class DailyResultsService {
  private readonly http = inject(HttpClient);
  private readonly baseUrl = `${environment.apiUrl}${environment.apis.nourishmentEngineAdmin}/v1/daily-results`;

  /**
   * Each named day with its stored result, or null where the engine has not scored it, in the order the days
   * were named. The engine has no route that lists a Youtopian's days, so each is read on its own. Any failure
   * other than an unscored day populates `error()`.
   */
  createDaysResource(key: () => DailyResultDaysKey | null) {
    return rxResource<DailyResultDay[] | null, DailyResultDaysKey | null>({
      params: () => key(),
      stream: ({ params }) => {
        if (!params) return of(null);
        if (params.dates.length === 0) return of([]);
        return forkJoin(params.dates.map((date) => this.readDay(params.youtopianId, date)));
      }
    });
  }

  /** The Youtopian's days still waiting in the rescore queue, with the reason the last attempt at each failed. */
  queuedDays(youtopianId: string): Observable<DailyResultsQueueState> {
    const params = new HttpParams().set('top', BACKLOG_PAGE);
    return this.http.get<ServiceResponseV1<DailyResultsBacklogV1>>(`${this.baseUrl}/backlog`, { params }).pipe(
      map((response) => {
        const rows = response?.data?.rows ?? [];
        const pending = rows.filter((row) => row.youtopianId.toLowerCase() === youtopianId.toLowerCase());
        return { pending, truncated: pending.length === 0 && rows.length >= BACKLOG_PAGE };
      }),
      catchError((error) => this.errorHandler(error, 'Error reading the daily results backlog'))
    );
  }

  /** Without a range the engine marks the look-back window, reckoned in each household's own local days. */
  rescore(youtopianId: string, range?: DailyResultsRescoreRange): Observable<DailyResultsRescoreResponseV1> {
    const params = range ? new HttpParams().set('from', range.from).set('to', range.to) : new HttpParams();
    return this.http
      .post<
        ServiceResponseV1<DailyResultsRescoreResponseV1>
      >(`${this.baseUrl}/youtopians/${youtopianId}/rescore`, null, { params })
      .pipe(
        map((response) => {
          if (!response?.data) throw new Error('Rescore response missing data');
          return response.data;
        }),
        catchError((error) => this.errorHandler(error, 'Error marking daily results for rescore'))
      );
  }

  simulate(youtopianId: string, request: DailyResultsSimulationRequestV1): Observable<DailyResultV1> {
    return this.http
      .post<ServiceResponseV1<DailyResultV1>>(`${this.baseUrl}/youtopians/${youtopianId}/simulate`, request)
      .pipe(
        map((response) => {
          if (!response?.data) throw new Error('Simulation response missing data');
          return response.data;
        }),
        catchError((error) => this.errorHandler(error, 'Error simulating a daily result'))
      );
  }

  private readDay(youtopianId: string, date: string): Observable<DailyResultDay> {
    return this.http.get<ServiceResponseV1<DailyResultV1>>(`${this.baseUrl}/youtopians/${youtopianId}/${date}`).pipe(
      map((response) => ({ date, result: response?.data ?? null })),
      catchError((err) => (isUnscoredDay(err) ? of({ date, result: null }) : this.handleResourceError(err)))
    );
  }

  private handleResourceError(err: unknown): Observable<never> {
    const error =
      err instanceof HttpErrorResponse
        ? new Error(`HTTP ${err.status}: ${err.message}`)
        : err instanceof Error
          ? err
          : new Error(String(err));
    return throwError(() => error);
  }

  private errorHandler(error: unknown, serviceMessage: string): Observable<never> {
    const err = error as { error?: { message?: string }; message?: string };
    console.error(`[DailyResultsService] ${serviceMessage}`, err?.error?.message ?? err?.message ?? error);
    return throwError(() => error);
  }
}
